import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './EcosystemPage.css';

const ventures = [
  {
    id: 'finance',
    orbit: '05',
    name: 'ProfitYo Finance',
    category: 'Finance',
    description:
      'Accounting, bookkeeping, tax, and financial services for growing businesses.',
    icon: FiIcons.FiBarChart2
  },
  {
    id: 'burning',
    orbit: '04',
    name: 'Burning Papers',
    category: 'Lifestyle',
    description:
      'A lifestyle brand focused on personal growth, transformation, and long-term thinking.',
    icon: FiIcons.FiFeather
  },
  {
    id: 'app',
    orbit: '03',
    name: 'ProfitYo App',
    category: 'Technology',
    description:
      'Technology solutions that automate workflows and support scalable growth.',
    icon: FiIcons.FiCpu
  },
  {
    id: 'partnerships',
    orbit: '02',
    name: 'Strategic Partnerships',
    category: 'Collaboration',
    description:
      'Strategic relationships that expand capabilities and unlock opportunities.',
    icon: FiIcons.FiShare2
  },
  {
    id: 'future',
    orbit: '01',
    name: 'Future Ventures',
    category: 'Innovation',
    description:
      'Emerging ventures and initiatives designed for future growth.',
    icon: FiIcons.FiZap
  }
];

const focusAreas = [
  ['Artificial Intelligence', FiIcons.FiCpu],
  ['Financial Innovation', FiIcons.FiTrendingUp],
  ['Technology', FiIcons.FiMonitor],
  ['Automation', FiIcons.FiZap],
  ['Education', FiIcons.FiBookOpen],
  ['Business Services', FiIcons.FiBriefcase],
  ['E-Commerce', FiIcons.FiShoppingBag],
  ['Emerging Brands', FiIcons.FiStar]
];

function SectionLabel({ children }) {
  return (
    <span className="ecosystem-section-label">
      {children}
    </span>
  );
}

function VentureCard({ venture, index }) {
  return (
    <motion.article
      className={`ecosystem-architecture-card orbit-card-${venture.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        delay: index * 0.08,
        duration: 0.45
      }}
      whileHover={{ y: -7 }}
    >
      <div className="ecosystem-card-top">
        <span className="ecosystem-card-icon">
          <SafeIcon icon={venture.icon} />
        </span>

        <span className="ecosystem-card-number">
          {venture.orbit}
        </span>
      </div>

      <span className="ecosystem-card-category">
        {venture.category}
      </span>

      <h3>{venture.name}</h3>

      <p>{venture.description}</p>

      <SafeIcon
        icon={FiIcons.FiArrowUpRight}
        className="ecosystem-card-arrow"
      />
    </motion.article>
  );
}

function OrbitRing({ venture }) {
  return (
    <div
      className={`ecosystem-orbit-ring orbit-ring-${venture.id}`}
      aria-hidden="true"
    >
      <span className="ecosystem-orbit-marker" />
    </div>
  );
}

function EcosystemMap() {
  return (
    <div className="ecosystem-architecture-map">
      <div className="ecosystem-map-grid" />

      <div className="ecosystem-map-glow ecosystem-map-glow-one" />
      <div className="ecosystem-map-glow ecosystem-map-glow-two" />

      <div
        className="ecosystem-orbit-system"
        aria-hidden="true"
      >
        {ventures.map((venture) => (
          <OrbitRing
            key={venture.id}
            venture={venture}
          />
        ))}
      </div>

      <svg
        className="ecosystem-architecture-connectors"
        viewBox="0 0 1000 760"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line
          x1="500"
          y1="380"
          x2="500"
          y2="74"
        />

        <line
          x1="500"
          y1="380"
          x2="855"
          y2="205"
        />

        <line
          x1="500"
          y1="380"
          x2="790"
          y2="645"
        />

        <line
          x1="500"
          y1="380"
          x2="210"
          y2="645"
        />

        <line
          x1="500"
          y1="380"
          x2="145"
          y2="205"
        />

        <circle
          cx="500"
          cy="380"
          r="6"
        />
      </svg>

      <motion.div
        className="ecosystem-architecture-hub"
        animate={{
          boxShadow: [
            '0 0 0 0 rgba(34,197,94,.18), 0 24px 70px rgba(4,24,48,.3)',
            '0 0 0 24px rgba(34,197,94,0), 0 24px 70px rgba(4,24,48,.3)',
            '0 0 0 0 rgba(34,197,94,0), 0 24px 70px rgba(4,24,48,.3)'
          ]
        }}
        transition={{
          duration: 3.8,
          repeat: Infinity
        }}
      >
        <strong>ProfitYo</strong>
        <b>Global System™</b>
        <i />
        <small>One vision · Multiple solutions</small>
      </motion.div>

      {ventures.map((venture, index) => (
        <VentureCard
          key={venture.id}
          venture={venture}
          index={index}
        />
      ))}
    </div>
  );
}

function FocusCard({ area, index }) {
  const [name, icon] = area;

  return (
    <motion.article
      className="ecosystem-focus-card"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{
        once: true,
        amount: 0.15
      }}
      transition={{
        delay: index * 0.05
      }}
      whileHover={{ y: -5 }}
    >
      <span className="ecosystem-focus-number">
        0{index + 1}
      </span>

      <span className="ecosystem-focus-icon">
        <SafeIcon icon={icon} />
      </span>

      <h3>{name}</h3>

      <SafeIcon
        icon={FiIcons.FiArrowUpRight}
        className="ecosystem-focus-arrow"
      />
    </motion.article>
  );
}

function HeroNetwork() {
  return (
    <div
      className="ecosystem-hero-network"
      aria-label="Connected ecosystem visualization"
    >
      <div className="hero-network-grid" />

      <div className="hero-network-orbit hero-network-orbit-wide" />
      <div className="hero-network-orbit hero-network-orbit-tall" />

      <svg
        className="hero-network-lines"
        viewBox="0 0 520 430"
        aria-hidden="true"
      >
        <line
          x1="260"
          y1="215"
          x2="90"
          y2="82"
        />

        <line
          x1="260"
          y1="215"
          x2="430"
          y2="82"
        />

        <line
          x1="260"
          y1="215"
          x2="90"
          y2="348"
        />

        <line
          x1="260"
          y1="215"
          x2="430"
          y2="348"
        />
      </svg>

      <div className="hero-network-core">
        

        <strong>
          BUILT TO
          <br />
          COMPOUND
        </strong>

        
      </div>

      <span className="hero-network-node hero-network-node-one">
        VISION
      </span>

      <span className="hero-network-node hero-network-node-two">
        SYSTEMS
      </span>

      <span className="hero-network-node hero-network-node-three">
        EXPERTISE
      </span>

      <span className="hero-network-node hero-network-node-four">
        IMPACT
      </span>
    </div>
  );
}

export default function EcosystemPage({ type }) {
  if (type !== 'ecosystem') {
    return null;
  }

  return (
    <div className="ecosystem-page">
      <section className="ecosystem-hero">
        <div className="container ecosystem-hero-layout">
          <motion.div
            className="ecosystem-hero-copy"
            initial={{
              opacity: 0,
              x: -22
            }}
            animate={{
              opacity: 1,
              x: 0
            }}
          >
            <SectionLabel>
              ProfitYo Global System™
            </SectionLabel>

            <h1>
              A growing ecosystem built{' '}
              <em>for long-term value.</em>
            </h1>

            <p>
              ProfitYo Global System brings together businesses,
              brands, strategic partnerships, technologies, and
              emerging ventures that work together to create
              opportunity, innovation, and sustainable growth.
            </p>

            <div className="ecosystem-hero-meta">
              <span />
              <b>
                Building businesses · Creating opportunities ·
                Growing systems
              </b>
            </div>
          </motion.div>

          <motion.div
            className="ecosystem-hero-visual"
            initial={{
              opacity: 0,
              scale: 0.9
            }}
            animate={{
              opacity: 1,
              scale: 1
            }}
            transition={{
              duration: 0.7
            }}
          >
            <HeroNetwork />
          </motion.div>
        </div>
      </section>

      <section className="ecosystem-introduction">
        <div className="container ecosystem-intro-layout">
          <div>
            <SectionLabel>
              One larger system
            </SectionLabel>

            <h2>
              Distinct companies.
              <br />
              <em>Shared direction.</em>
            </h2>
          </div>

          <p>
            Every venture is designed to strengthen the whole.
            Shared knowledge, strategic relationships, technology,
            and long-term thinking create a foundation for
            compounding value.
          </p>

          <span className="ecosystem-intro-mark">
            01
          </span>
        </div>
      </section>

      <section className="ecosystem-map-section">
        <div className="container">
          <div className="ecosystem-section-heading">
            <div>
              <SectionLabel>
                Portfolio architecture
              </SectionLabel>

              <h2>
                Connected by purpose.
                <br />
                <em>Built for what is next.</em>
              </h2>
            </div>

            <p>
              Five dedicated orbit paths show how every venture
              connects directly to ProfitYo Global System™ while
              maintaining its own role.
            </p>
          </div>

          <EcosystemMap />
        </div>
      </section>

      <section className="ecosystem-horizon-section">
        <div className="container">
          <div className="ecosystem-section-heading ecosystem-horizon-heading">
            <div>
              <SectionLabel>
                Opportunity horizon
              </SectionLabel>

              <h2>
                Designed to keep
                <br />
                <em>expanding.</em>
              </h2>
            </div>

            <p>
              The ecosystem has room for new sectors,
              technologies, and partnerships that align with our
              long-term vision.
            </p>
          </div>

          <div className="ecosystem-focus-grid">
            {focusAreas.map((area, index) => (
              <FocusCard
                key={area[0]}
                area={area}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="ecosystem-principles-section">
        <div className="container ecosystem-principles-layout">
          <div>
            <SectionLabel>
              How the system creates value
            </SectionLabel>

            <h2>
              Built to create
              <br />
              <em>durable momentum.</em>
            </h2>
          </div>

          <div className="ecosystem-principles-list">
            <div>
              <span>01</span>
              <strong>
                Build strong foundations
              </strong>
              <small>
                Systems and expertise create confidence before
                scale.
              </small>
            </div>

            <div>
              <span>02</span>
              <strong>
                Connect complementary capabilities
              </strong>
              <small>
                Shared knowledge makes every part of the
                ecosystem stronger.
              </small>
            </div>

            <div>
              <span>03</span>
              <strong>
                Invest in useful innovation
              </strong>
              <small>
                Technology and ideas stay grounded in
                real-world value.
              </small>
            </div>

            <div>
              <span>04</span>
              <strong>
                Think beyond the next milestone
              </strong>
              <small>
                Long-term decisions create resilient businesses
                and lasting impact.
              </small>
            </div>
          </div>
        </div>
      </section>

      <section className="ecosystem-closing">
        <div className="container">
          <SectionLabel>
            Our enduring commitment
          </SectionLabel>

          <h2>
            Building businesses. Creating opportunities.
            <br />
            <em>Growing systems that last.</em>
          </h2>

          <p>
            One long-term vision, expressed through multiple
            solutions and connected by a shared commitment to
            meaningful growth.
          </p>
        </div>
      </section>
    </div>
  );
}